import React, { useState } from 'react';
import { X, Plus, Save } from 'lucide-react';
import { AccountType, OverdraftType } from '../types';
import { createAccount } from '../services/financeService';
import { useFirebase } from './FirebaseProvider';
import { motion, AnimatePresence } from 'motion/react';
import { INSTITUTIONS } from '../constants/institutions';

interface AddAccountModalProps {
  onClose: () => void;
}

export const AddAccountModal: React.FC<AddAccountModalProps> = ({ onClose }) => {
  const { user, effectiveUserId } = useFirebase();
  const [name, setName] = useState('');
  const [type, setType] = useState<AccountType>('EFECTIVO');
  const [institutionId, setInstitutionId] = useState<string>('efectivo');
  const [initialBalance, setInitialBalance] = useState<number>(0);
  
  const [creditLimit, setCreditLimit] = useState<number>(0);
  const [overdraftType, setOverdraftType] = useState<OverdraftType>('NONE');
  const [overdraftValue, setOverdraftValue] = useState<number>(0);
  
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveUserId || !name.trim()) return;

    setIsSaving(true);
    try {
      await createAccount(effectiveUserId, {
        name: name.trim(),
        type,
        initialBalance,
        institutionId: institutionId || null,
        creditLimit: (type === 'TARJETA DE CREDITO' || type === 'BANCOS') ? creditLimit : 0,
        overdraftType: (type === 'TARJETA DE CREDITO' || type === 'BANCOS') ? overdraftType : 'NONE',
        overdraftValue: (type === 'TARJETA DE CREDITO' || type === 'BANCOS') ? overdraftValue : 0,
      });
      onClose();
    } catch (error) {
      console.error('Error creating account:', error);
      alert('Hubo un error al crear la cuenta.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
      >
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-xl font-bold text-zinc-100 flex items-center gap-2">
            <Plus className="text-emerald-500" size={24} />
            Nuevo Medio de Pago
          </h2>
          <button 
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-xl transition-all"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Nombre de la Cuenta
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-white focus:outline-none focus:border-emerald-500 transition-colors"
                placeholder="Ej: Billetera, Banco Itaú..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Tipo de Cuenta
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as AccountType)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-white focus:outline-none focus:border-emerald-500 transition-colors appearance-none"
              >
                <option value="EFECTIVO">Efectivo</option>
                <option value="BANCOS">Bancos</option>
                <option value="TARJETA DE CREDITO">Tarjeta de Crédito</option>
                <option value="OTROS">Otros</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Entidad Financiera
              </label>
              <select
                value={institutionId}
                onChange={(e) => setInstitutionId(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-white focus:outline-none focus:border-emerald-500 transition-colors appearance-none"
              >
                {INSTITUTIONS.map(inst => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Saldo Inicial
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-bold">Gs.</span>
                <input
                  type="number"
                  value={initialBalance}
                  onChange={(e) => setInitialBalance(Number(e.target.value))}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 pl-12 text-white focus:outline-none focus:border-emerald-500 transition-colors font-mono"
                  placeholder="0"
                  required
                />
              </div>
              <p className="text-xs text-zinc-500 mt-2 italic">
                Este será el saldo base de tu cuenta al momento de crearla.
              </p>
            </div>

            {(type === 'TARJETA DE CREDITO' || type === 'BANCOS') && (
              <div className="p-4 bg-zinc-800/30 border border-zinc-800 rounded-xl space-y-4">
                <h3 className="text-sm font-bold text-zinc-300">Configuración de Límites</h3>
                
                {type === 'TARJETA DE CREDITO' && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">
                      Línea de Crédito Total
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-bold">Gs.</span>
                      <input
                        type="number"
                        value={creditLimit}
                        onChange={(e) => setCreditLimit(Number(e.target.value))}
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 pl-12 text-white focus:outline-none focus:border-emerald-500 transition-colors font-mono"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                )}
                
                {type === 'BANCOS' && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">
                      Línea de Sobregiro Principal
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-bold">Gs.</span>
                      <input
                        type="number"
                        value={creditLimit}
                        onChange={(e) => setCreditLimit(Number(e.target.value))}
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 pl-12 text-white focus:outline-none focus:border-emerald-500 transition-colors font-mono"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">
                    Línea Excedente / Segundo Sobregiro
                  </label>
                  <select
                    value={overdraftType}
                    onChange={(e) => setOverdraftType(e.target.value as OverdraftType)}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-white focus:outline-none focus:border-emerald-500 transition-colors appearance-none mb-3"
                  >
                    <option value="NONE">Sin sobregiro adicional</option>
                    <option value="FIXED">Importe Fijo</option>
                    {type === 'TARJETA DE CREDITO' && <option value="PERCENTAGE">Porcentaje de Línea</option>}
                  </select>

                  <AnimatePresence>
                    {overdraftType !== 'NONE' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                      >
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-bold">
                            {overdraftType === 'PERCENTAGE' ? '%' : 'Gs.'}
                          </span>
                          <input
                            type="number"
                            value={overdraftValue}
                            onChange={(e) => setOverdraftValue(Number(e.target.value))}
                            className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 pl-12 text-white focus:outline-none focus:border-emerald-500 transition-colors font-mono"
                            placeholder="0"
                            min="0"
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl font-medium transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving || !name.trim()}
              className="flex-1 py-3 px-4 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2"
            >
              <Save size={18} />
              {isSaving ? 'Creando...' : 'Crear Cuenta'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};
